import { t, useLanguage } from '../../i18n';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { Linking, View, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions, Alert, Platform, LayoutChangeEvent, SafeAreaView, Text } from 'react-native';
import { useTheme, Card, ActivityIndicator, Button, Snackbar, Modal, TextInput } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { Course } from '../../types';
import { useSchedule } from '../../contexts/ScheduleContext';
import { useSyllabus } from '../../contexts/SyllabusContext';
import { getDisplayColor } from '../../utils/courseColors';
import { useReviewGateContext } from '../../contexts/ReviewGateContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { getCurrentSemester, isInReviewPeriod } from '../../constants/reviewPeriods';
import { getOfferingKey, offeringDocument } from '../../utils/courseIdentity';
import { calendarCourseKey } from '../../utils/calendarExport';
import NewFeatureAnnouncement from '../../components/NewFeatureAnnouncement';

type ScheduleScreenProps = {
  navigation: any;
};

const ScheduleScreen = ({ navigation }: ScheduleScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [containerHeight, setContainerHeight] = useState(0);

  const theme = useTheme();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const { academicYear, semester } = getCurrentSemester();
  // スケジュールコンテキストを使用
  const { userCourses, removeCourse, updateCourse } = useSchedule();
  // The timetable does not need the full syllabus until a legacy course is opened for editing.
  const { syllabusCourses, ensureSyllabusLoaded } = useSyllabus(false);

  // レビューゲートフック
  const { isGated, reviewCount, requiredCount, currentSemester } = useReviewGateContext();

  // 年度付きのレビュー済み講義キーのセット
  const [reviewedOfferingKeys, setReviewedOfferingKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedCourse || (selectedCourse.initialName && selectedCourse.initialRoom) || !syllabusCourses.length) return;
    const original = syllabusCourses.find(course => getOfferingKey(course) === getOfferingKey(selectedCourse));
    if (original) {
      setSelectedCourse(course => {
        if (!course || course.id !== selectedCourse.id) return course;
        const initialName = course.initialName || original.name;
        const initialRoom = course.initialRoom || original.room;
        if (course.initialName === initialName && course.initialRoom === initialRoom) return course;
        return { ...course, initialName, initialRoom };
      });
    }
  }, [selectedCourse, syllabusCourses]);

  // レビュー済みコースを取得（現在の時間割にある実授業を対象とする）
  useEffect(() => {
    const fetchReviewedCourses = async () => {
      if (!auth.currentUser || !currentSemester.academicYear) return;

      try {
        const courses = userCourses.filter((c) => !c.isCustom);

        const offeringKeys = new Set<string>();
        for (const course of courses) {
          const reviewDoc = await getDoc(doc(offeringDocument(db, course), 'reviews', auth.currentUser.uid));
          if (!reviewDoc.exists()) continue;
          const data = reviewDoc.data() as { academicYear?: string; semester?: string };
          if (data.academicYear === currentSemester.academicYear && data.semester === currentSemester.semester) {
            offeringKeys.add(getOfferingKey(course));
          }
        }
        setReviewedOfferingKeys(offeringKeys);
      } catch (error) {
        console.error('[ScheduleScreen] Failed to fetch reviewed courses:', error);
      }
    };

    fetchReviewedCourses();
  }, [currentSemester, userCourses]);

  // 編集用のローカル状態
  const [editName, setEditName] = useState('');
  const [editRoom, setEditRoom] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAbsentCount, setEditAbsentCount] = useState(0);
  const [editColor, setEditColor] = useState('#808080');

  const [memoSelection, setMemoSelection] = useState({ start: 0, end: 0 });
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [editLinkText, setEditLinkText] = useState('');
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isColorPickerExpanded, setIsColorPickerExpanded] = useState(false);
  const pendingNotesRef = React.useRef<string | null>(null);
  const initialMemoSelectionRef = React.useRef<{ start: number; end: number } | undefined>(undefined);

  const handleInsertBullet = () => {
    const bullet = '* ';
    const notes = editNotes || '';
    const start = memoSelection.start;

    // 現在の行の開始位置を探す
    const lastNewline = notes.lastIndexOf('\n', start - 1);
    const lineStart = lastNewline === -1 ? 0 : lastNewline + 1;
    const currentLine = notes.substring(lineStart, notes.indexOf('\n', lineStart) === -1 ? notes.length : notes.indexOf('\n', lineStart));

    if (currentLine.startsWith(bullet)) {
      // 箇条書きを削除
      const before = notes.substring(0, lineStart);
      const after = notes.substring(lineStart + bullet.length);
      setEditNotes(before + after);
    } else {
      // 箇条書きを追加
      const before = notes.substring(0, start);
      const after = notes.substring(memoSelection.end);
      const needsNewline = before.length > 0 && !before.endsWith('\n');
      const insertText = needsNewline ? '\n' + bullet : bullet;
      setEditNotes(before + insertText + after);
    }
  };

  const handleNotesChange = (text: string) => {
    // handleKeyPress がすでにBackspaceを処理した場合、ネイティブの変更を無視して
    // 意図した値に上書きする（二重削除バグの防止）
    if (pendingNotesRef.current !== null) {
      setEditNotes(pendingNotesRef.current);
      pendingNotesRef.current = null;
      return;
    }

    const bulletPrefix = '* ';
    const prevNotes = editNotes || '';

    // Check for "Enter" key behavior (detecting newline addition)
    if (text.length > prevNotes.length) {
      const charPos = memoSelection.start;
      if (text.charAt(charPos) === '\n') {
        const lastNewlineBefore = prevNotes.lastIndexOf('\n', charPos - 1);
        const lineStart = lastNewlineBefore === -1 ? 0 : lastNewlineBefore + 1;
        const prevLine = prevNotes.substring(lineStart, charPos);

        if (prevLine.startsWith(bulletPrefix)) {
          // もし前の行が記号しかなかった場合、Enterで箇条書きを終了する
          if (prevLine === bulletPrefix) {
            const before = text.substring(0, lineStart);
            const after = text.substring(charPos + 1);
            setEditNotes(before + after);
            return;
          }
          // 次の行に自動的に箇条書き記号を付与
          const before = text.substring(0, charPos + 1);
          const after = text.substring(charPos + 1);
          setEditNotes(before + bulletPrefix + after);
          return;
        }
      }
    }

    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      const bulletIndex = line.indexOf(bulletPrefix);
      if (bulletIndex > 0) {
        // 箇条書き記号より前に文字がある場合、記号を先頭に移動する
        const before = line.substring(0, bulletIndex);
        const after = line.substring(bulletIndex + bulletPrefix.length);
        return bulletPrefix + before + after;
      }
      return line;
    });
    setEditNotes(processedLines.join('\n'));
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === 'Backspace') {
      const bulletPrefix = '* ';
      const notes = editNotes || '';
      const { start, end } = memoSelection;

      if (start === end) {
        const lastNewline = notes.lastIndexOf('\n', start - 1);
        const lineStart = lastNewline === -1 ? 0 : lastNewline + 1;
        const currentLineHead = notes.substring(lineStart, start);

        if (currentLineHead === bulletPrefix) {
          // 記号の直後で消すと記号をまるごと消す（スマート削除）
          // pendingNotesRef にセットして handleNotesChange の二重削除を防ぐ
          const before = notes.substring(0, lineStart);
          const after = notes.substring(start);
          const newNotes = before + after;
          pendingNotesRef.current = newNotes;
          setEditNotes(newNotes);
        }
      }
    }
  };

  const handleOpenLinkModal = () => {
    // もし選択範囲があればそれを表示テキストとしてあらかじめ入れる
    const selection = editNotes.substring(memoSelection.start, memoSelection.end);
    setEditLinkText(selection || '');
    setEditLinkUrl('');
    setLinkModalVisible(true);
  };

  const handleConfirmLink = () => {
    if (!editLinkUrl) {
      setLinkModalVisible(false);
      return;
    }

    const displayText = editLinkText || editLinkUrl;
    const template = `[${displayText}](${editLinkUrl})`;

    const before = editNotes.substring(0, memoSelection.start);
    const after = editNotes.substring(memoSelection.end);

    // 前後にスペースが必要か判定
    const needsStartSpacing = before.length > 0 && !before.endsWith('\n') && !before.endsWith(' ');
    const insertText = needsStartSpacing ? ' ' + template : template;

    setEditNotes(before + insertText + after);
    setLinkModalVisible(false);
    setEditLinkText('');
    setEditLinkUrl('');
  };

  const extractLinks = (text: string) => {
    const links = [];
    // 1. Markdown spec: [text](url)
    const mdRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    let match;
    while ((match = mdRegex.exec(text)) !== null) {
      links.push({ displayText: match[1], url: match[2] });
    }

    // 2. Raw URLs (excluding those already matched in markdown)
    // To do this simply without complex regex lookarounds, we replace matched MD links first
    const textWithoutMd = text.replace(mdRegex, '');
    const rawUrlRegex = /(https?:\/\/[^\s]+)/g;
    while ((match = rawUrlRegex.exec(textWithoutMd)) !== null) {
      links.push({ displayText: match[1], url: match[1] });
    }

    return links;
  };

  const extractedLinks = extractLinks(editNotes || '');

  const renderRichMemo = (text: string) => {
    if (!text) return null;

    const lines = text.split('\n');
    const bulletPrefix = '* ';

    return (
      <View style={styles.richMemoContainer}>
        {lines.map((line, lineIdx) => {
          const isBullet = line.startsWith(bulletPrefix);
          const content = isBullet ? line.substring(bulletPrefix.length) : line;

          // Parse links within the content
          const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g;
          const parts = [];
          let lastIndex = 0;
          let match;

          while ((match = combinedRegex.exec(content)) !== null) {
            if (match.index > lastIndex) {
              parts.push(content.substring(lastIndex, match.index));
            }

            if (match[1]) {
              parts.push({ type: 'link', name: match[1], url: match[2] });
            } else {
              parts.push({ type: 'link', name: match[3], url: match[3] });
            }
            lastIndex = match.index + match[0].length;
          }

          if (lastIndex < content.length) {
            parts.push(content.substring(lastIndex));
          }

          const renderedPart = (
            <Text key={`line-${lineIdx}`} style={[styles.richTextBase, { color: theme.colors.onSurface }]}>
              {parts.map((part, i) => {
                if (typeof part === 'string') {
                  return part;
                } else {
                  return (
                    <Text
                      key={`link-${lineIdx}-${i}`}
                      style={{ color: theme.colors.primary, textDecorationLine: 'underline' }}
                      onPress={() => Linking.openURL(part.url)}
                    >
                      {part.name}
                    </Text>
                  );
                }
              })}
            </Text>
          );

          if (isBullet) {
            return (
              <View key={`bullet-row-${lineIdx}`} style={styles.bulletRow}>
                <View style={styles.bulletIconContainer}>
                  <Ionicons
                    name="ellipse"
                    size={6}
                    color={theme.colors.onSurface}
                    style={styles.bulletIcon}
                  />
                </View>
                <View style={styles.bulletContent}>
                  {renderedPart}
                </View>
              </View>
            );
          }

          return <View key={`row-${lineIdx}`} style={styles.normalRow}>{renderedPart}</View>;
        })}
      </View>
    );
  };

  const PRESET_COLORS = [
    '#E54D42', // Red
    '#40C057', // Green
    '#339AF0', // Light Blue
    '#005BB5', // Dark Blue
    '#003366', // Navy
    '#15AABF', // Cyan (was Blue)
    '#FF4D94', // Pink
    '#D6336C', // Rose
    '#862E9C', // Purple
    '#808080', // Gray
    '#FAB005', // Yellow
    '#FF8C00'  // Orange
  ];

  // Days of the week in Japanese
  const days = ['月', '火', '水', '木', '金'];
  // Number of periods (時限)
  const periods = [1, 2, 3, 4, 5, 6];
  // 時間帯の表示
  const periodTimes: { [key: number]: string } = {
    1: '08:30',
    2: '10:10',
    3: '12:40',
    4: '14:20',
    5: '16:00',
    6: '17:40'
  };

  useEffect(() => {
    // ユーザーの授業が読み込まれたらローディングを終了
    setLoading(false);
  }, [userCourses]);

  // オンデマンド授業かどうかの判定
  const isOnDemandCourse = (course: Course): boolean => {
    // 時限が1-6の範囲外、または曜日が月-金の範囲外
    const isOutOfGrid = !course.period || course.period < 1 || course.period > 6 ||
      course.dayOfWeek === undefined || course.dayOfWeek < 1 || course.dayOfWeek > 5;

    // または名前にオンデマンドが含まれる
    const hasOnDemandTag = (course.name || '').includes('オンデマンド') ||
      (course.name || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.room || '').includes('オンデマンド') ||
      (course.room || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.notes || '').includes('オンデマンド') ||
      (course.notes || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ');

    return isOutOfGrid || hasOnDemandTag;
  };

  const onDemandCourses = userCourses.filter(isOnDemandCourse);
  const gridCourses = userCourses.filter(c => !isOnDemandCourse(c));

  const onDemandRows = onDemandCourses.length > 0 ? Math.ceil(onDemandCourses.length / 4) : 0;

  const getCourseByDayAndPeriod = (day: number, period: number): Course | undefined => {
    //引数 day (0-indexed) を 1-indexed に変換して比較
    const targetDayOfWeek = day + 1;
    const foundCourse = gridCourses.find(course =>
      course.dayOfWeek === targetDayOfWeek && course.period === period
    );
    return foundCourse;
  };

  // 授業を削除する
  const handleRemoveCourse = (courseId: string) => {
    const performDelete = () => {
      removeCourse(courseId);
      setSnackbarMessage(t("授業を時間割から削除しました"));
      setSnackbarVisible(true);
    };

    if (Platform.OS === 'web') {
      if (window.confirm(t("この授業を時間割から削除しますか？"))) {
        performDelete();
      }
    } else {
      Alert.alert(
        t("授業を削除"),
        t("この授業を時間割から削除しますか？"),
        [
          {
            text: t("キャンセル"),
            style: 'cancel',
          },
          {
            text: t("削除"),
            onPress: performDelete,
            style: 'destructive',
          },
        ]
      );
    }
  };


  const handleCellPress = (course: Course | undefined) => {
    if (course) {
      if (!course.isCustom && (!course.initialName || !course.initialRoom) && !syllabusCourses.length) {
        ensureSyllabusLoaded();
      }
      // 通常の授業詳細モーダル
      // If initialName or initialRoom is missing (for existing courses),
      // look them up in the syllabus database
      const syllabusCourse = (!course.initialName || !course.initialRoom)
        ? syllabusCourses.find(c => getOfferingKey(c) === getOfferingKey(course))
        : null;

      const courseWithDefaults = {
        ...course,
        initialName: course.initialName || syllabusCourse?.name || course.name,
        initialRoom: course.initialRoom || syllabusCourse?.room || course.room,
      };

      setSelectedCourse(courseWithDefaults);
      setEditName(course.name || '');
      setEditRoom(course.room || '');
      setEditNotes(course.notes || '');
      setEditAbsentCount(course.absentClasses || 0);
      // Use the same color logic as the grid for initial modal color
      setEditColor(getDisplayColor(course) || '#808080');
      setIsColorPickerExpanded(false);
      setIsEditingNotes(false);
      setModalVisible(true);
    }
  };

  const handleSaveChanges = async () => {
    if (selectedCourse) {
      // If the name or room is empty, fall back to the initial syllabus data
      const finalName = editName.trim() || selectedCourse.initialName || selectedCourse.name || '無題の授業';
      const finalRoom = editRoom.trim() || selectedCourse.initialRoom || selectedCourse.room || '';

      await updateCourse(selectedCourse.id, {
        name: finalName,
        room: finalRoom,
        notes: editNotes,
        absentClasses: editAbsentCount,
        color: editColor,
      });
      setModalVisible(false);
      setSnackbarMessage(t("変更を保存しました"));
      setSnackbarVisible(true);
    }
  };

  const renderTimeTable = () => {
    if (containerHeight === 0) return null; // Wait for layout to measure available space

    const horizontalPadding = 8;
    // セルの最小幅を設定 (拡大時に文字が縦に並ぶのを防ぐ)
    const MIN_COLUMN_WIDTH = 75;
    const totalGridWidth = Math.max(screenWidth - (horizontalPadding * 2), MIN_COLUMN_WIDTH * 5);
    const columnWidth = totalGridWidth / 5;

    const scaleFactor = Math.min(2.0, Math.max(1.0, screenWidth / 390));

    const dayHeaderHeight = Math.max(22, Math.floor(22 * scaleFactor)); // minimized
    // オンデマンドエリアの固定高さ (通常セルと同じ高さ)
    const onDemandHeaderHeight = onDemandCourses.length > 0 ? 36 : 0;

    const paddingBuffer = 4;

    // availableHeight considers the main grid (Periods 1-6) and the on-demand header
    // so the header is visible at the bottom of the first screen.
    const availableHeight = containerHeight - dayHeaderHeight - onDemandHeaderHeight - paddingBuffer;

    // 登録されている授業から最大時限を計算 (最小5限)
    const maxPeriod = Math.max(5, ...gridCourses.map(c => c.period || 1));
    const activePeriods = Array.from({ length: maxPeriod }, (_, i) => i + 1);

    const startTimeRowHeight = Math.max(12, Math.floor(12 * scaleFactor)); // minimized

    // Calculate the mathematical fluid height.
    // When on-demand exists, we divide by a slightly larger number to leave space for the "peek" (divider + 1/3 block).
    // Using a slightly smaller addition (0.3 instead of 0.4) because the text header is gone.
    const divisor = onDemandCourses.length > 0 ? maxPeriod + 0.3 : maxPeriod;
    const fluidRowHeight = Math.floor(availableHeight / divisor);
    const MIN_ROW_HEIGHT = 60; // Fallback minimum height
    const periodRowHeight = Math.max(MIN_ROW_HEIGHT, fluidRowHeight);

    const cellHeight = periodRowHeight - startTimeRowHeight;

    const fontSizeRoom = Math.max(9, Math.floor(9 * scaleFactor));
    const paddingVerticalRoom = Math.max(4, Math.floor(4 * scaleFactor));
    const roomHeaderMinHeight = fontSizeRoom + (paddingVerticalRoom * 2);
    const headerFontSize = Math.max(10, Math.floor(10 * scaleFactor));
    const timeFontSize = Math.max(8, Math.floor(8 * scaleFactor));

    const getAdaptiveNameStyle = (name: string) => {
      let baseSize = 10 * scaleFactor;
      if (name.length > 18) baseSize = 8 * scaleFactor;
      else if (name.length > 10) baseSize = 9 * scaleFactor;

      const fontSize = Math.floor(baseSize);
      return { fontSize, lineHeight: Math.floor(fontSize * 1.1) };
    };

    const isScrollable = totalGridWidth > (screenWidth - (horizontalPadding * 2));

    return (
      <View style={styles.tableContainer}>
        <ScrollView
          horizontal
          scrollEnabled={isScrollable}
          showsHorizontalScrollIndicator={false}
          bounces={false}
        >
          <View style={{ width: totalGridWidth }}>
            {/* Header row with days */}
            <View style={[styles.headerRow, { borderBottomColor: theme.colors.outlineVariant }]}>
              {days.map((day, index) => (
                <View
                  key={`day-header-${index}`}
                  style={[styles.dayCell, { width: columnWidth, height: dayHeaderHeight }]}
                >
                  <Text style={[styles.dayText, { fontSize: headerFontSize, color: theme.colors.onSurfaceVariant }]}>{t(day)}</Text>
                </View>
              ))}
            </View>

            {/* Period rows with start times */}
            {activePeriods.map((period) => (
              <React.Fragment key={`period-fragment-${period}`}>
                {/* Start Time Row */}
                <View style={[styles.startTimeRow, { height: startTimeRowHeight }]}>
                  <Text style={[styles.startTimeText, { fontSize: timeFontSize, color: theme.colors.onSurfaceVariant }]}>{periodTimes[period] || ''}</Text>
                </View>

                {/* Course Cells Row */}
                <View key={`period-cells-${period}`} style={styles.periodRow}>
                  {days.map((dayString, dayIndex) => {
                    const course = getCourseByDayAndPeriod(dayIndex, period);
                    return (
                      <TouchableOpacity
                        key={`cell-${dayIndex}-${period}`}
                        style={[styles.courseCellTouchable, { width: columnWidth, height: cellHeight }]}
                        onPress={() => {
                          if (course) handleCellPress(course);
                        }}
                        activeOpacity={course ? 0.7 : 1}
                      >
                        {course ? (
                          <View style={[styles.courseCellCard, { backgroundColor: theme.colors.elevation.level1 }]}>
                            <View style={[styles.roomContainer, {
                              backgroundColor: getDisplayColor(course) || '#808080',
                              paddingVertical: paddingVerticalRoom,
                              minHeight: roomHeaderMinHeight
                            }]}> 
                              {!!course.room && (
                                <Text style={[styles.roomText, { fontSize: fontSizeRoom }]} numberOfLines={1}>{course.room}</Text>
                              )}
                            </View>
                            <View style={styles.courseNameContainer}>
                              <Text
                                style={[
                                  styles.courseNameText,
                                  getAdaptiveNameStyle(course.name),
                                  { color: theme.colors.onSurface }
                                ]}
                                numberOfLines={4}
                              >
                                {course.name}
                              </Text>
                            </View>
                          </View>
                        ) : (
                          <View style={styles.emptyCourseCellCard} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </React.Fragment>
            ))}

            {/* On-demand courses section */}
            {onDemandCourses.length > 0 && (
              <View style={styles.onDemandSection}>
                <View style={[styles.onDemandDivider, { backgroundColor: theme.colors.outlineVariant }]} />
                <View style={styles.onDemandGrid}>
                  {onDemandCourses.map((course) => (
                    <TouchableOpacity
                      key={`on-demand-${course.isCustom ? `custom_${course.id}` : getOfferingKey(course)}`}
                      style={[styles.onDemandItem, { width: columnWidth, height: cellHeight }]}
                      onPress={() => handleCellPress(course)}
                    >
                      <View style={[styles.courseCellCard, { backgroundColor: theme.colors.elevation.level1 }]}>
                        <View style={[styles.roomContainer, {
                          backgroundColor: getDisplayColor(course) || '#808080',
                          paddingVertical: paddingVerticalRoom,
                          minHeight: roomHeaderMinHeight
                        }]}> 
                          {!!course.room && (
                            <Text style={[styles.roomText, { fontSize: fontSizeRoom }]} numberOfLines={1}>{course.room}</Text>
                          )}
                        </View>
                        <View style={styles.courseNameContainer}>
                          <Text
                            style={[
                              styles.courseNameText,
                              getAdaptiveNameStyle(course.name),
                              { color: theme.colors.onSurface }
                            ]}
                            numberOfLines={4}
                          >
                            {course.name}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* レビューバナー（ゲート中のみ表示） */}
      {isGated && userCourses.length > 0 && (
        <TouchableOpacity
          onPress={() => navigation.navigate('ReviewManagement')}
          style={[styles.reviewBanner, { backgroundColor: theme.colors.primaryContainer }]}
          activeOpacity={0.7}
        >
          <Ionicons name="information-circle" size={20} color={theme.colors.primary} />
          <Text style={[styles.reviewBannerText, { color: theme.colors.onPrimaryContainer }]}>
            {t('{0}年度{1}の講義レビューを投稿してください（{2}/{3}件完了）', [academicYear, t(semester), reviewCount, requiredCount])}
          </Text>
          <Ionicons name="chevron-forward" size={16} color={theme.colors.primary} />
        </TouchableOpacity>
      )}

      <ScrollView
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 4, paddingTop: 2 }}
        showsVerticalScrollIndicator={false}
        bounces={false}
        onLayout={(e: LayoutChangeEvent) => setContainerHeight(e.nativeEvent.layout.height)}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={{ marginTop: 16, color: theme.colors.onSurface }}>
              {t("時間割を読み込み中...")}
            </Text>
          </View>
        ) : (
          <>
            {userCourses.length > 0 ? (
              renderTimeTable()
            ) : (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: theme.colors.onSurface }]}>
                  {t("時間割に授業が登録されていません")}
                </Text>
                <Text style={[styles.emptySubText, { color: theme.colors.onSurfaceVariant }]}>
                  {t("「履修科目検索」から授業を追加してください")}
                </Text>
              </View>
            )}
          </>
        )}
      </ScrollView>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={[styles.snackbar, { backgroundColor: theme.colors.inverseSurface }]}
      >
        <Text style={{ color: theme.colors.inverseOnSurface }}>{snackbarMessage}</Text>
      </Snackbar>

      <NewFeatureAnnouncement
        enabled={!modalVisible && !linkModalVisible}
        onOpenCalendar={() => navigation.navigate('CalendarExport')}
      />

      {/* Course Detail Modal */}
      {selectedCourse && (
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={[styles.modalContent, { backgroundColor: theme.colors.elevation.level3 }]}
        >
          <SafeAreaView style={{ flex: 1 }}>
            <View style={[styles.modalHeader, { paddingHorizontal: 20, paddingTop: 8, paddingBottom: 4, alignItems: 'center', justifyContent: 'center' }]}>
              <TouchableOpacity onPress={() => setModalVisible(false)} style={{ position: 'absolute', right: 20, zIndex: 10 }}>
                <Ionicons name="close" size={28} color={theme.colors.onSurface} />
              </TouchableOpacity>
              <Text style={[styles.modalHeaderTitle, { color: theme.colors.onSurface }]}>{t("授業詳細")}</Text>
            </View>

            <ScrollView contentContainerStyle={styles.modalScrollContent} showsVerticalScrollIndicator={false}>
              <View style={styles.fieldContainer}>
                <Text style={[styles.fieldLabel, { color: theme.colors.onSurface }]}>{t("授業名")}</Text>
                <TextInput
                  mode="flat"
                  value={editName}
                  onChangeText={setEditName}
                  placeholder={t("授業名を追加")}
                  onBlur={() => {
                    if (!editName.trim() && selectedCourse) {
                      setEditName(selectedCourse.initialName || selectedCourse.name || '');
                    }
                  }}
                  style={[styles.textInput, styles.modernInput, { color: theme.colors.onSurface, backgroundColor: theme.colors.surfaceVariant }]}
                  underlineColor="transparent"
                  activeUnderlineColor="transparent"
                  placeholderTextColor={theme.colors.onSurfaceVariant}
                />
              </View>

              <View style={styles.fieldContainer}>
                <Text style={[styles.fieldLabel, { color: theme.colors.onSurface }]}>{t("教室名")}</Text>
                <TextInput
                  mode="flat"
                  value={editRoom}
                  onChangeText={setEditRoom}
                  onBlur={() => {
                    if (!editRoom.trim() && selectedCourse) {
                      setEditRoom(selectedCourse.initialRoom || selectedCourse.room || '');
                    }
                  }}
                  style={[styles.textInput, styles.modernInput, { color: theme.colors.onSurface, backgroundColor: theme.colors.surfaceVariant }]}
                  underlineColor="transparent"
                  activeUnderlineColor="transparent"
                />
              </View>

              <View style={styles.fieldContainer}>
                <Text style={[styles.fieldLabel, { color: theme.colors.onSurface }]}>{t("メモ")}</Text>

                <View style={[styles.memoContainer, { backgroundColor: theme.colors.surfaceVariant, borderWidth: 0 }]}>
                  {/* Text area */}
                  {isEditingNotes ? (
                    <TextInput
                      mode="flat"
                      value={editNotes}
                      onChangeText={handleNotesChange}
                      onKeyPress={handleKeyPress}
                      onSelectionChange={(e) => {
                        initialMemoSelectionRef.current = undefined;
                        setMemoSelection(e.nativeEvent.selection);
                      }}
                      onBlur={() => setIsEditingNotes(false)}
                      selection={initialMemoSelectionRef.current}
                      autoFocus
                      multiline
                      numberOfLines={4}
                      placeholder={t("メモ/リンクを追加...")}
                      style={[styles.memoTextInput, { color: theme.colors.onSurface, backgroundColor: 'transparent' }]}
                      underlineColor="transparent"
                      activeUnderlineColor="transparent"
                      placeholderTextColor={theme.colors.onSurfaceVariant}
                      dense
                    />
                  ) : (
                    <TouchableOpacity
                      activeOpacity={1}
                      onPress={() => {
                        const len = (editNotes || '').length;
                        initialMemoSelectionRef.current = { start: len, end: len };
                        setIsEditingNotes(true);
                      }}
                      style={styles.memoReadArea}
                    >
                      <ScrollView showsVerticalScrollIndicator={false} nestedScrollEnabled>
                        {editNotes ? (
                          renderRichMemo(editNotes)
                        ) : (
                          <Text style={[styles.memoPlaceholder, { color: theme.colors.onSurfaceVariant }]}>
                            {t("メモ/リンクを追加...")}
                          </Text>
                        )}
                      </ScrollView>
                    </TouchableOpacity>
                  )}

                  {/* Bottom toolbar */}
                  <View style={styles.memoBottomToolbar}>
                    <TouchableOpacity onPress={handleOpenLinkModal} style={styles.toolbarButton}>
                      <Ionicons name="link" size={20} color={theme.colors.onSurfaceVariant} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={handleInsertBullet} style={styles.toolbarButton}>
                      <Ionicons name="list" size={20} color={theme.colors.onSurfaceVariant} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <View style={[styles.modernSection, { backgroundColor: theme.colors.surfaceVariant }]}>
                <View style={styles.sectionHeaderLeft}>
                  <Text style={[styles.sectionLabel, { color: theme.colors.onSurface, marginBottom: 0 }]}>{t("欠席回数")}</Text>
                </View>
                <View style={[styles.modernCounterRow, { backgroundColor: theme.colors.surface }]}>
                  <TouchableOpacity
                    style={[styles.modernCounterButton, editAbsentCount <= 0 && { opacity: 0.3 }]}
                    onPress={() => setEditAbsentCount(Math.max(0, editAbsentCount - 1))}
                    disabled={editAbsentCount <= 0}
                  >
                    <Ionicons name="remove" size={20} color={theme.colors.primary} />
                  </TouchableOpacity>
                  <Text style={[styles.modernCounterValue, { color: theme.colors.onSurface }]}>{editAbsentCount}</Text>
                  <TouchableOpacity
                    style={[styles.modernCounterButton, editAbsentCount >= 15 && { opacity: 0.3 }]}
                    onPress={() => setEditAbsentCount(Math.min(15, editAbsentCount + 1))}
                    disabled={editAbsentCount >= 15}
                  >
                    <Ionicons name="add" size={20} color={theme.colors.primary} />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.colorSection}>
                <Text style={[styles.fieldLabel, { color: theme.colors.onSurface }]}>{t("授業の色")}</Text>
                <TouchableOpacity
                  style={[styles.modernSection, { backgroundColor: theme.colors.surfaceVariant, marginTop: 4 }]}
                  onPress={() => setIsColorPickerExpanded(!isColorPickerExpanded)}
                  activeOpacity={0.7}
                >
                  <View style={styles.colorPickerToggleLeft}>
                    <View style={[styles.currentColorPreview, { backgroundColor: editColor }]} />
                    <Text style={[styles.colorNameText, { color: theme.colors.onSurface }]}>
                      {editColor === '#E54D42' ? t("レッド") :
                        editColor === '#40C057' ? t("グリーン") :
                          editColor === '#339AF0' ? t("ライトブルー") :
                            editColor === '#005BB5' ? t("ダークブルー") :
                              editColor === '#003366' ? t("ネイビー") :
                                editColor === '#15AABF' ? t("シアン") :
                                  editColor === '#FF4D94' ? t("ピンク") :
                                    editColor === '#D6336C' ? t("ローズ") :
                                      editColor === '#862E9C' ? t("パープル") :
                                        editColor === '#808080' ? t("グレー") :
                                          editColor === '#FAB005' ? t("イエロー") :
                                            editColor === '#FF8C00' ? t("オレンジ") : t("カスタム")}
                    </Text>
                  </View>
                  <Ionicons
                    name={isColorPickerExpanded ? "chevron-up" : "chevron-down"}
                    size={20}
                    color={theme.colors.onSurfaceVariant}
                  />
                </TouchableOpacity>

                {isColorPickerExpanded && (
                  <View style={styles.colorGrid}>
                    {PRESET_COLORS.map((color) => (
                      <TouchableOpacity
                        key={color}
                        style={[
                          styles.colorCircle,
                          { backgroundColor: color },
                          editColor === color && [styles.colorCircleSelected, { borderColor: theme.colors.primary }]
                        ]}
                        onPress={() => {
                          setEditColor(color);
                          // Optional: close on selection
                          // setIsColorPickerExpanded(false);
                        }}
                      />
                    ))}
                  </View>
                )}
              </View>

              <Button
                mode="contained"
                onPress={handleSaveChanges}
                style={[styles.saveButton, { backgroundColor: theme.colors.primary }]}
                labelStyle={styles.saveButtonLabel}
              >
                {t("変更を保存")}
              </Button>

              <Button
                mode="outlined"
                onPress={() => {
                  setModalVisible(false);
                  navigation.navigate('CalendarExport', { courseSlotKey: calendarCourseKey(selectedCourse) });
                }}
                style={[styles.syllabusButton, { borderColor: theme.colors.primary }]}
                icon={({ size, color }) => <Ionicons name="calendar-outline" size={size} color={color} />}
              >
                {t('Googleカレンダーに追加')}
              </Button>

              {!selectedCourse.isCustom && (
                <>
                  <Button
                    mode="outlined"
                    onPress={() => {
                      setModalVisible(false);
                      navigation.navigate('CourseReviewForm', { course: selectedCourse });
                    }}
                    style={[styles.syllabusButton, { borderColor: theme.colors.primary }]}
                    textColor={theme.colors.primary}
                    icon={({ size, color }) => <Ionicons name="pencil" size={size} color={color} />}
                    disabled={!isInReviewPeriod()}
                  >
                    {t("レビューを書く")}
                  </Button>
                  <Button
                    mode="outlined"
                    onPress={() => {
                      setModalVisible(false);
                      navigation.navigate('CourseEvaluation', { course: selectedCourse });
                    }}
                    style={[styles.syllabusButton, { borderColor: theme.colors.primary }]}
                    textColor={theme.colors.primary}
                    icon={({ size, color }) => <Ionicons name="stats-chart" size={size} color={color} />}
                  >
                    {t("授業評価を見る")}
                  </Button>
                </>
              )}

              {!!selectedCourse.syllabusUrl && (
                <Button
                  mode="outlined"
                  onPress={() => {
                    if (selectedCourse.syllabusUrl) {
                      Linking.openURL(selectedCourse.syllabusUrl);
                    }
                  }}
                  style={[styles.syllabusButton, { borderColor: theme.colors.primary }]}
                  textColor={theme.colors.primary}
                  icon={({ size, color }) => <Ionicons name="open-outline" size={size} color={color} />}
                >
                  {t("シラバスを見る")}
                </Button>
              )}

              <TouchableOpacity
                onPress={() => {
                  handleRemoveCourse(selectedCourse.id);
                  setModalVisible(false);
                }}
                style={styles.deleteLink}
              >
                <Text style={styles.deleteLinkText}>{t("この授業を削除")}</Text>
              </TouchableOpacity>
            </ScrollView>
          </SafeAreaView>
        </Modal>
      )}

      {/* Link Edit Modal */}
      <Modal
        visible={linkModalVisible}
        onDismiss={() => setLinkModalVisible(false)}
        contentContainerStyle={[styles.linkModalContent, { backgroundColor: theme.colors.elevation.level5 }]}
      >
        <View style={styles.linkModalInner}>
          <Text style={[styles.linkModalTitle, { color: theme.colors.onSurface }]}>{t("リンクの編集")}</Text>

          <View style={styles.linkInputRow}>
            <Ionicons name="menu-outline" size={24} color={theme.colors.onSurfaceVariant} style={styles.linkInputIcon} />
            <TextInput
              placeholder={t("表示するテキスト")}
              value={editLinkText}
              onChangeText={setEditLinkText}
              mode="flat"
              style={[styles.linkTextInput, { backgroundColor: theme.colors.elevation.level1 }]}
              placeholderTextColor={theme.colors.onSurfaceVariant}
              underlineColor="transparent"
              activeUnderlineColor={theme.colors.primary}
            />
          </View>

          <View style={styles.linkInputRow}>
            <Ionicons name="earth-outline" size={22} color={theme.colors.onSurfaceVariant} style={styles.linkInputIcon} />
            <TextInput
              placeholder={t("URL アドレスへのリンク")}
              value={editLinkUrl}
              onChangeText={setEditLinkUrl}
              mode="flat"
              style={[styles.linkTextInput, { backgroundColor: theme.colors.elevation.level1 }]}
              placeholderTextColor={theme.colors.onSurfaceVariant}
              underlineColor="transparent"
              activeUnderlineColor={theme.colors.primary}
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          <View style={styles.linkModalButtons}>
            <Button
              onPress={() => setLinkModalVisible(false)}
              textColor={theme.colors.primary}
            >
              {t("キャンセル")}
            </Button>
            <Button
              mode="contained"
              onPress={handleConfirmLink}
              disabled={!editLinkUrl}
              style={styles.linkConfirmButton}
            >
              OK
            </Button>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: 'flex',
  },
  scrollViewContent: {
    flexGrow: 1,
    paddingHorizontal: 8,
    paddingTop: 2,
    paddingBottom: Platform.OS === 'ios' ? 140 : 100, // accommodate higher nav bar fully
  },
  tableContainer: {
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  dayCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 2, // extreme compression
    height: 22,
  },
  dayText: {
    fontSize: 10,
  },
  periodRow: {
    flexDirection: 'row',
  },
  startTimeRow: {
    paddingVertical: 0,
    height: 12, // extreme compression
    alignItems: 'center',
    justifyContent: 'center',
  },
  startTimeSlot: {
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  startTimeText: {
    fontSize: 8,
    paddingLeft: 2,
  },
  periodNumberCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  periodNumberText: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  emptyCourseCellCard: {
    flex: 1,
    backgroundColor: 'transparent',
    borderRadius: 8,
  },
  courseCellTouchable: {
    padding: 4,
  },
  courseCellCard: {
    flex: 1,
    borderRadius: 8,
    elevation: 2,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 1.5,
    overflow: 'hidden',
  },
  roomContainer: {
    paddingVertical: 5,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  courseNameContainer: {
    paddingHorizontal: 6,
    paddingVertical: 4,
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  roomText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
  },
  courseNameText: {
    fontSize: 10,
    textAlign: 'center',
    lineHeight: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubText: {
    fontSize: 14,
    textAlign: 'center',
  },
  snackbar: {
    backgroundColor: '#333',
  },
  courseNameInputContainer: {
    marginBottom: 8,
  },
  courseNameInput: {
    fontSize: 20,
    fontWeight: 'bold',
    paddingHorizontal: 0,
    paddingVertical: 0,
    paddingTop: 0,
    paddingBottom: 0,
    height: 36,
  },
  modernInput: {
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 44,
    fontSize: 16,
    justifyContent: 'center',
    marginTop: 4,
  },
  modernSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
  },
  sectionHeaderLeft: {
    flex: 1,
  },
  sectionSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  modernCounterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    padding: 4,
    gap: 16,
  },
  modernCounterButton: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCounterValue: {
    fontSize: 18,
    fontWeight: 'bold',
    minWidth: 20,
    textAlign: 'center',
  },
  colorNameText: {
    fontSize: 16,
    marginLeft: 12,
    fontWeight: '500',
  },
  fieldContainer: {
    marginBottom: 12,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
    paddingLeft: 4,
  },
  memoContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 4,
    minHeight: 100,
  },
  memoTextInput: {
    fontSize: 16,
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 60,
  },
  memoReadArea: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    minHeight: 60,
  },
  memoPlaceholder: {
    fontSize: 16,
  },
  memoBottomToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 16,
  },
  modalContent: {
    flex: 1,
    margin: 0,
    borderRadius: 0,
    overflow: 'hidden',
  },
  modalScrollContent: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 10,
    flexGrow: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  modalHeaderTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    position: 'absolute',
    top: -10,
    left: 12,
    paddingHorizontal: 4,
    zIndex: 1,
    fontSize: 12,
  },
  textInput: {
    backgroundColor: 'transparent',
    fontSize: 16,
  },
  textInputOutline: {
    borderRadius: 8,
  },
  textArea: {
    height: 120,
  },
  memoToolbar: {
    position: 'absolute',
    top: -12,
    right: 12,
    flexDirection: 'row',
    zIndex: 2,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  toolbarButton: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    marginLeft: 4,
  },
  richTextContainer: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    overflow: 'hidden',
  },
  richTextBase: {
    fontSize: 16,
    lineHeight: 22,
  },
  richMemoContainer: {
    paddingBottom: 8,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingLeft: 4,
    marginBottom: 4,
  },
  bulletIconContainer: {
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulletIcon: {
    marginTop: 8,
  },
  bulletContent: {
    flex: 1,
  },
  normalRow: {
    marginBottom: 4,
  },
  extractedLinksContainer: {
    marginTop: 8,
    gap: 6,
  },
  extractedLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 8,
  },
  extractedLinkText: {
    fontSize: 14,
    flex: 1,
    textDecorationLine: 'underline',
  },
  counterSection: {
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  counterRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  counterButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  counterButtonPlus: {
    backgroundColor: '#f06292',
  },
  counterButtonText: {
    fontSize: 24,
    color: '#333',
  },
  counterButtonTextPlus: {
  },
  counterValue: {
    marginHorizontal: 20,
    fontSize: 20,
    fontWeight: 'bold',
  },
  colorSection: {
    marginBottom: 20,
    backgroundColor: 'transparent',
  },
  colorPickerToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  colorPickerToggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  currentColorPreview: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginTop: 16,
    paddingHorizontal: 8,
  },
  colorCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    margin: 4,
    borderColor: 'transparent',
  },
  colorCircleSelected: {
    borderColor: '#f06292',
  },
  saveButton: {
    marginTop: 4,
    borderRadius: 8,
    backgroundColor: '#f06292',
  },
  saveButtonLabel: {
    fontSize: 16,
    paddingVertical: 4,
  },
  syllabusButton: {
    marginTop: 8,
    borderRadius: 8,
    borderColor: '#f06292',
  },
  deleteLink: {
    marginTop: 8,
    alignItems: 'center',
    paddingVertical: 8,
  },
  deleteLinkText: {
    color: '#FF5252',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  linkModalContent: {
    margin: 32,
    borderRadius: 24,
    overflow: 'hidden',
  },
  linkModalInner: {
    padding: 24,
  },
  linkModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 24,
  },
  linkInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  linkInputIcon: {
    width: 24,
  },
  linkTextInput: {
    flex: 1,
    height: 48,
    borderRadius: 4,
  },
  linkModalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 16,
    gap: 8,
  },
  linkConfirmButton: {
    borderRadius: 20,
    paddingHorizontal: 8,
  },
  onDemandSection: {
    marginTop: 0,
    paddingTop: 0,
  },
  onDemandDivider: {
    height: 1,
    width: '100%',
    marginVertical: 8,
  },
  onDemandGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  onDemandItem: {
    padding: 4,
  },
  reviewBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  reviewBannerText: {
    fontSize: 14,
    flex: 1,
  },
});

export default ScheduleScreen;
